// Saved-chat state for the SQ-AI page — the listing in the rail, and the one transcript being read.
//
// Lifted out of SqAiPage for the same reason usePicker is: it is HTTP/service-coupled, and it is a
// separate concern from the conversation the SDK owns. Nothing here touches the live thread —
// reading a saved chat is a second, read-only surface beside it, never a way into it.
//
// ★ THE STORE IS WRITTEN ASYNCHRONOUSLY, so a refetch fired the instant a turn finishes normally
//   MISSES that turn. The path is engine → AMQ topic → durable subscription → HTTP POST → Mongo, and
//   the UI is not on it. Hence REFRESH_DELAY_MS below: the reload after a turn is deliberately late.
//   The rail does not depend on it for the chat in progress — it renders that row from local state
//   (HistoryRail), so a slow or failed delivery costs a row's metadata, never the row.
//
// ★ SCOPED SERVER-SIDE, NOT HERE. The endpoint filters on the caller's own user_id off the token;
//   this module passes a tenant and nothing else. Never add a userId parameter — an identity the
//   client supplies is one the client can change.

import { useCallback, useEffect, useRef, useState } from "react";
import { chatHistoryApi } from "../../services/api";

// How long to wait after a turn completes before refetching the listing. Long enough for the
// broker round trip to land the two message events, short enough that the row's title and count
// appear while the reader is still looking at the answer that produced them. A miss is not a
// failure — the next reload picks it up.
const REFRESH_DELAY_MS = 2500;

// How many rows a page of the rail asks for. Smaller than the server's own default (50) because the
// rail is a narrow column: a page should be a scroll or two, not a wall. The server clamps anything
// above 100, so this is a preference, not a limit.
const PAGE_SIZE = 25;

// How many TURNS a page of one transcript asks for. Turns, not messages — the server pages by turn
// so a boundary can never fall between a question and its answer.
const TRANSCRIPT_PAGE_TURNS = 5;

function errorText(err, fallback) {
  const said = err && err.response && err.response.data && err.response.data.error;
  return said || (err && err.message) || fallback;
}

// `everyone` (the "Mine only" box unticked — RSM4, design v2 D21; on or off the resume switch): the listing holds every internal user's chats,
// each naming its owner. False lists the caller's own, exactly as before.
export function useChatHistory({ tenantSchema, everyone = false }) {
  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);
  // The server's opaque cursor for the NEXT page, or null at the end of the listing. Held in a ref
  // rather than state because nothing renders it and `loadMore` must read the current value at call
  // time — a captured one would re-request the page it just fetched.
  const nextCursorRef = useRef(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // A next page that failed to load. Kept apart from `listError` so the rows already read stay on screen: the rail shows a Load more
  // button and the scroll stops asking until it is pressed.
  const [moreFailed, setMoreFailed] = useState(false);
  // Invalidates in-flight pages. A tenant change or a reload makes every page already fetched stale,
  // so a response that arrives after one must not append onto the new list.
  const listGenRef = useRef(0);

  const [viewingId, setViewingId] = useState(null);
  const [viewTitle, setViewTitle] = useState("");
  const [viewMessages, setViewMessages] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState(null);
  // The open failed because the chat is not the reader's to see or is not there (404/403), as against a failed read.
  const [viewUnavailable, setViewUnavailable] = useState(false);
  // Whose the open chat is — { name, self } — when it was read with `everyone` (RSM4); null otherwise and until it has loaded.
  const [viewOwner, setViewOwner] = useState(null);
  // Paging one transcript. `hasEarlier` is the server's own answer; the cursor walks BACKWARDS in
  // time, so a page fetched with it is PREPENDED rather than appended.
  const earlierCursorRef = useRef(null);
  const [hasEarlier, setHasEarlier] = useState(false);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  // The same for the turns above the ones on screen: the transcript stays and shows a retry at its top.
  const [earlierFailed, setEarlierFailed] = useState(false);

  // Bumped to force a refetch of the listing without changing the tenant.
  const [reloadTick, setReloadTick] = useState(0);
  const reload = useCallback(() => setReloadTick((n) => n + 1), []);

  const timerRef = useRef(null);
  // A turn's events land after the turn does, so this is the only reload the page asks for at
  // end-of-turn. Rescheduling on each call keeps a burst of turns to one refetch.
  const reloadSoon = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setReloadTick((n) => n + 1);
    }, REFRESH_DELAY_MS);
  }, []);
  useEffect(() => () => timerRef.current && clearTimeout(timerRef.current), []);

  // The listing. Keyed on the tenant because the tenant IS the collection the read opens — a
  // tenant change is a different set of chats, not a filter over the same one.
  useEffect(() => {
    if (!tenantSchema) {
      setList([]);
      setListError(null);
      return undefined;
    }
    const gen = ++listGenRef.current;
    let cancelled = false;
    setListLoading(true);
    setListError(null);
    // Page 1 REPLACES rather than appends, and resets the cursor. This is also why a post-turn
    // refresh does not try to preserve the pages already loaded: the turn moved its conversation to
    // the top of the ordering, so every cursor taken from the old order now points into a list that
    // has shifted under it — re-paging from the start is the correct answer, not the lazy one.
    nextCursorRef.current = null;
    setHasMore(false);
    setMoreFailed(false);
    chatHistoryApi
      .list(tenantSchema, { limit: PAGE_SIZE, everyone })
      .then((res) => {
        if (cancelled || gen !== listGenRef.current) return;
        const data = res.data || {};
        setList(Array.isArray(data.conversations) ? data.conversations : []);
        nextCursorRef.current = data.nextCursor || null;
        // `nextCursor: null` is the server saying "last page" — it is always present, so this never
        // has to infer the end from a short page.
        setHasMore(Boolean(data.nextCursor));
        setListLoading(false);
      })
      .catch((err) => {
        if (cancelled || gen !== listGenRef.current) return;
        // 503 is the store being unconfigured, which is a deployment state and not the user's
        // problem to interpret — said plainly so an empty rail is never mistaken for no history.
        setListError(
          err && err.response && err.response.status === 503
            ? "Chat history isn't available on this deployment."
            : errorText(err, "Could not load saved chats")
        );
        setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantSchema, reloadTick, everyone]);

  // The next page, APPENDED. Guarded on `loadingMore` so a double-click cannot request the same
  // cursor twice and append the page twice — the cursor only advances once a response lands.
  const loadMore = useCallback(() => {
    const cursor = nextCursorRef.current;
    if (!tenantSchema || !cursor || loadingMore) return;
    const gen = listGenRef.current;
    setLoadingMore(true);
    setMoreFailed(false);
    chatHistoryApi
      .list(tenantSchema, { limit: PAGE_SIZE, cursor, everyone })
      .then((res) => {
        if (gen !== listGenRef.current) return;
        const data = res.data || {};
        const rows = Array.isArray(data.conversations) ? data.conversations : [];
        // Appended by id, not blindly concatenated. The cursor makes a duplicate impossible on a
        // stable list, but a conversation that gained a turn between two pages moves in the
        // ordering, and defending the render against that costs one Set.
        setList((prev) => {
          const have = new Set(prev.map((c) => c.conversationId));
          return prev.concat(rows.filter((c) => !have.has(c.conversationId)));
        });
        nextCursorRef.current = data.nextCursor || null;
        setHasMore(Boolean(data.nextCursor));
        setLoadingMore(false);
      })
      .catch((err) => {
        if (gen !== listGenRef.current) return;
        setMoreFailed(true);
        setLoadingMore(false);
      });
  }, [tenantSchema, loadingMore, everyone]);

  // One transcript. A generation ref, not just a cancelled flag: opening B while A is in flight
  // must not let A's response paint over B.
  const openGenRef = useRef(0);
  // Whether the open transcript was read with `everyone`, so its earlier pages are read the same way (another user's chat
  // has none of the caller's own documents to page through).
  const everyoneViewRef = useRef(false);

  // A 404 or 403 reads the same either way — the endpoint answers 404 alike for a chat that was never saved, one that no longer
  // exists and one that belongs to someone else — as `viewUnavailable`, the page's cue to keep the text box (RSM10).
  // `everyone` (RSM4) reads any internal user's chat and learns whose it is (`viewOwner`), so the page continues only its own.
  const open = useCallback(
    (conversationId, title, { everyone: anyOwner } = {}) => {
      if (!tenantSchema || !conversationId) return;
      const gen = ++openGenRef.current;
      setViewingId(conversationId);
      setViewTitle(title || "");
      setViewMessages([]);
      setViewError(null);
      setViewUnavailable(false);
      setViewOwner(null);
      setViewLoading(true);
      earlierCursorRef.current = null;
      setHasEarlier(false);
      // (an earlier page still being read for the chat this replaces is dropped by the generation above, so its flag goes with it — left
      // set, this chat's "load earlier" could never be pressed: RSM5)
      setLoadingEarlier(false);
      setEarlierFailed(false);
      everyoneViewRef.current = Boolean(anyOwner);
      chatHistoryApi
        .get(tenantSchema, conversationId, { limit: TRANSCRIPT_PAGE_TURNS, everyone: anyOwner })
        .then((res) => {
          if (gen !== openGenRef.current) return;
          const data = res.data || {};
          setViewMessages(Array.isArray(data.messages) ? data.messages : []);
          setViewOwner(data.owner && typeof data.owner === "object" ? data.owner : null);
          earlierCursorRef.current = data.nextCursor || null;
          setHasEarlier(Boolean(data.nextCursor));
          setViewLoading(false);
        })
        .catch((err) => {
          if (gen !== openGenRef.current) return;
          const gone = Boolean(err && err.response && (err.response.status === 404 || err.response.status === 403));
          setViewUnavailable(gone);
          setViewError(gone ? "This chat isn't available to you." : errorText(err, "Could not load that chat"));
          setViewLoading(false);
        });
    },
    [tenantSchema]
  );

  // The turns BEFORE the ones on screen, PREPENDED. Guarded on `loadingEarlier` so a double-click
  // cannot request the same cursor twice; the generation ref is the open transcript's, so a page
  // arriving after the reader has opened a different chat is dropped rather than mixed into it.
  const loadEarlier = useCallback(() => {
    const cursor = earlierCursorRef.current;
    if (!tenantSchema || !viewingId || !cursor || loadingEarlier) return;
    const gen = openGenRef.current;
    setLoadingEarlier(true);
    setEarlierFailed(false);
    chatHistoryApi
      .get(tenantSchema, viewingId, { limit: TRANSCRIPT_PAGE_TURNS, cursor, everyone: everyoneViewRef.current })
      .then((res) => {
        if (gen !== openGenRef.current) return;
        const data = res.data || {};
        const rows = Array.isArray(data.messages) ? data.messages : [];
        // PREPENDED, and de-duplicated by message id for the same reason the listing is: the cursor
        // makes a repeat impossible on a stable transcript, and the guard costs one Set.
        setViewMessages((prev) => {
          const have = new Set(prev.map((m) => m.id));
          return rows.filter((m) => !have.has(m.id)).concat(prev);
        });
        earlierCursorRef.current = data.nextCursor || null;
        setHasEarlier(Boolean(data.nextCursor));
        setLoadingEarlier(false);
      })
      .catch((err) => {
        if (gen !== openGenRef.current) return;
        setEarlierFailed(true);
        setLoadingEarlier(false);
      });
  }, [tenantSchema, viewingId, loadingEarlier]);

  // The transcript on screen, and putting one back as it was (RSM4): the page keeps the continued chat's in one slot and shows it again
  // when the reader goes back to that chat from another one while its live thread has messages — not re-read then, so the turns asked
  // since (the live thread's) are not added.
  const snapshot = useCallback(
    () => ({ id: viewingId, title: viewTitle, messages: viewMessages, owner: viewOwner, nextCursor: earlierCursorRef.current, everyone: everyoneViewRef.current }),
    [viewingId, viewTitle, viewMessages, viewOwner]
  );
  const restore = useCallback((snap) => {
    openGenRef.current += 1;
    setViewingId(snap.id);
    setViewTitle(snap.title || "");
    setViewMessages(snap.messages);
    setViewOwner(snap.owner);
    setViewError(null);
    setViewUnavailable(false);
    setViewLoading(false);
    setLoadingEarlier(false);
    setEarlierFailed(false);
    earlierCursorRef.current = snap.nextCursor;
    setHasEarlier(Boolean(snap.nextCursor));
    everyoneViewRef.current = snap.everyone;
  }, []);

  const close = useCallback(() => {
    // Invalidates any in-flight open, so a response arriving after the reader has gone back to the
    // live thread cannot reopen the view they just left.
    openGenRef.current += 1;
    earlierCursorRef.current = null;
    setHasEarlier(false);
    setLoadingEarlier(false);
    setEarlierFailed(false);
    setViewingId(null);
    setViewTitle("");
    setViewMessages([]);
    setViewError(null);
    setViewUnavailable(false);
    setViewOwner(null);
    setViewLoading(false);
  }, []);

  // A tenant change makes the open transcript belong to a collection this page is no longer reading.
  useEffect(() => {
    close();
  }, [tenantSchema, close]);

  return {
    list,
    listLoading,
    listError,
    hasMore,
    loadingMore,
    moreFailed,
    loadMore,
    reload,
    reloadSoon,
    viewingId,
    viewTitle,
    viewMessages,
    viewLoading,
    viewError,
    viewUnavailable,
    viewOwner,
    hasEarlier,
    loadingEarlier,
    earlierFailed,
    loadEarlier,
    open,
    snapshot,
    restore,
    close,
  };
}
